import CloudRoundedIcon from "@mui/icons-material/CloudRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import { Box, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { Canvas, Edge, Node } from "reaflow";
import { EDGE_COLORS, STATUS_COLORS, UI } from "../tokens";
import NodeContent from "./NodeContent";

// Breathing room added around the ELK bounding box so the outermost nodes and edges
// are not flush against the edge of the scrollable area.
const CANVAS_PADDING = 80;

// Height of the scrollable graph viewport, leaving room for the app header, the
// breadcrumb row, the object chips and the legend rendered above it.
const GRAPH_VIEWPORT_HEIGHT = "calc(100vh - 20em)";

// Edge stroke styles, colored from the design-token edge palette.
const EDGE_STYLE = `
  .edge, .edgeStopped, .noPoliciesEdge, .noPoliciesEdgeStopped, .errorEdge {
    stroke-linecap: round;
  }
  .edge {
    stroke: ${EDGE_COLORS.ready};
    stroke-dasharray: 6;
    animation: dashdraw .6s linear infinite;
    stroke-width: 2.5;
  }
  .edgeStopped {
    stroke: ${EDGE_COLORS.ready};
    stroke-width: 2.5;
  }
  .errorEdge {
    stroke: ${EDGE_COLORS.alert};
    stroke-width: 3;
  }
  .noPoliciesEdge {
    stroke: ${EDGE_COLORS.noPolicies};
    stroke-width: 2.5;
    stroke-dasharray: 6;
    animation: dashdraw .6s linear infinite;
  }
  .noPoliciesEdgeStopped {
    stroke: ${EDGE_COLORS.noPolicies};
    stroke-width: 2.5;
  }
  @keyframes dashdraw {
    0% { stroke-dashoffset: 12; }
  }
`;

function LegendDot({ color, bordered }) {
  return (
    <Box
      sx={{
        width: 11,
        height: 11,
        borderRadius: "50%",
        backgroundColor: color,
        border: bordered ? `1px solid ${UI.textMuted}` : "none",
        flexShrink: 0,
      }}
    />
  );
}

function LegendItem({ children, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
      {children}
      <Typography sx={{ fontSize: 12, color: UI.textSecondary }}>{label}</Typography>
    </Box>
  );
}

function LegendDivider() {
  return <Box sx={{ width: "1px", height: 16, backgroundColor: UI.border }} />;
}

function GraphLegend() {
  const typeIconSx = { fontSize: 15, color: UI.textMuted };
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.75,
        px: 2,
        py: 1,
        mb: 1,
        backgroundColor: UI.surfaceMuted,
        border: `1px solid ${UI.border}`,
        borderRadius: 2,
      }}
    >
      <LegendItem label="Completed">
        <LegendDot color={STATUS_COLORS.ready.fill} />
      </LegendItem>
      <LegendItem label="Processing">
        <LegendDot color={STATUS_COLORS.processing.fill} bordered />
      </LegendItem>
      <LegendItem label="No alert policy">
        <LegendDot color={STATUS_COLORS.noPolicies.fill} />
      </LegendItem>
      <LegendItem label="Alert">
        <LegendDot color={STATUS_COLORS.alert.fill} />
      </LegendItem>
      <LegendDivider />
      <LegendItem label="Completed">
        <Box sx={{ width: 22, borderTop: `2.5px solid ${EDGE_COLORS.neutral}` }} />
      </LegendItem>
      <LegendItem label="In progress">
        <Box sx={{ width: 22, borderTop: `2.5px dashed ${EDGE_COLORS.neutral}` }} />
      </LegendItem>
      <LegendDivider />
      <LegendItem label="Robot">
        <SmartToyRoundedIcon sx={typeIconSx} />
      </LegendItem>
      <LegendItem label="Vertex AI">
        <HubRoundedIcon sx={typeIconSx} />
      </LegendItem>
      <LegendItem label="Cloud Run">
        <CloudRoundedIcon sx={typeIconSx} />
      </LegendItem>
    </Box>
  );
}

function getNodeStatus(node) {
  if (node.alerts?.length > 0) return "alert";
  const hasPolicies = node.policiesConfigurated || node.root;
  if (!hasPolicies) return "noPolicies";
  return node.data?.ready ? "ready" : "processing";
}

function getEdgeClassName(edge) {
  if (edge.data.alerts?.length > 0) return "errorEdge";
  if (edge.data.policiesConfigurated) {
    return edge.data.stopped ? "edgeStopped" : "edge";
  }
  return edge.data.stopped ? "noPoliciesEdgeStopped" : "noPoliciesEdge";
}

export default function MonitoringGraph(props) {
  const { nodes, edges, clickable = true, onNodeClick, onEdgeClick } = props;

  // reaflow renders the SVG at exactly maxWidth x maxHeight and the container scrolls
  // no further than that box, so the canvas has to be sized from the layout ELK
  // actually produced. Any fixed value silently clips (and makes unreachable) every
  // pipeline that outgrows it. Starts at zero because there is nothing to scroll until
  // ELK reports its first layout.
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // reaflow pins every node to ELK portConstraints=FIXED_ORDER, which locks each
  // node's edge attach points to the edges' array order. When two parents feed one
  // child, that order is reversed vs. their left→right positions, so the edges swap
  // sides and cross just above the child. Relaxing to FREE lets ELK order the attach
  // points to minimize crossings. (node.layoutOptions overrides reaflow's default.)
  const layoutNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        layoutOptions: { portConstraints: "FREE", ...(node.layoutOptions || {}) },
      })),
    [nodes]
  );

  const handleLayoutChange = useCallback((layout) => {
    if (!layout?.width || !layout?.height) return;

    const width = Math.ceil(layout.width + CANVAS_PADDING);
    const height = Math.ceil(layout.height + CANVAS_PADDING);

    setCanvasSize((currentSize) =>
      currentSize.width === width && currentSize.height === height
        ? currentSize
        : { width, height }
    );
  }, []);

  return (
    <>
      <style>{EDGE_STYLE}</style>
      <GraphLegend />
      <Canvas
        className="yScrollBarWrapper-grey"
        onLayoutChange={handleLayoutChange}
        nodes={layoutNodes}
        edges={edges}
        direction="DOWN"
        // Overrides (merged over reaflow's ELK defaults, key by key) to stop sibling
        // edges that feed a shared child from swapping sides and crossing just above it.
        // semiInteractive lets crossing-minimization honor each node's horizontal
        // position, so a left parent attaches left and a right parent attaches right.
        layoutOptions={{
          'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
          'elk.layered.crossingMinimization.semiInteractive': 'true',
          'elk.layered.thoroughness': '50',
        }}
        defaultPosition="top left"
        width={"100%"}
        height={GRAPH_VIEWPORT_HEIGHT}
        maxWidth={canvasSize.width}
        maxHeight={canvasSize.height}
        pannable={true}
        readonly={true}
        arrow={null}
        // Access to edges in session view
        edge={(edge, idx) => (
          <Edge
            className={getEdgeClassName(edge)}
            key={idx}
            onClick={(e, data) => onEdgeClick(data)}
          />
        )}
        node={
          <Node style={{ fill: "transparent", strokeWidth: 0 }}>
            {(event, idx) => (
              <foreignObject
                key={idx}
                onClick={() => {
                  if (clickable) {
                    onNodeClick(event.node.id, event.node);
                  }
                }}
                display={"flex"}
                height={event.height}
                width={event.width}
                x={0}
                y={0}
              >
                <NodeContent
                  status={getNodeStatus(event.node)}
                  displayName={event.node.data.displayName}
                  description={event.node.data.description}
                  granularity={event.node.data.granularity}
                  progress={event.node.data.progress}
                  maxValue={event.node.data.maxValue}
                  root={event.node.root}
                  id={event.node.id}
                  serviceName={event.node.data.serviceName}
                  isVertex={event.node.data.isVertex}
                  project={event.node.data.projectId}
                />
              </foreignObject>
            )}
          </Node>
        }
      />
    </>
  );
}
